import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  NotesContainer,
  ContentSection,
  SectionCard,
  SectionTitle,
  SummaryTextArea,
  ActionBar,
  ActionButton,
  UpdatesList,
  UpdateItem,
  UpdateIcon,
  UpdateContent,
  UpdateTitle,
  UpdateText,
  TaskList,
  TaskItem,
  Checkbox,
  TaskInput,
  AddButton,
  Footer,
  SaveButton,
} from "./NotesManager.styled";

const NotesManager = ({
  summary,
  onSummaryChange,
  updates,
  onUpdatesChange,
  tasks,
  onTasksChange,
  onSave,
  readOnly,
}) => {
  // Local state just for new task input
  const [newTask, setNewTask] = useState("");

  // Task actions
  const handleAddTask = () => {
    if (!newTask.trim()) return;
    onTasksChange([
      ...tasks,
      { id: `t${Date.now()}`, text: newTask.trim(), done: false },
    ]);
    setNewTask("");
  };

  const handleTaskToggle = (idx) => {
    onTasksChange(
      tasks.map((t, i) => (i === idx ? { ...t, done: !t.done } : t)),
    );
  };

  const handleTaskEdit = (idx, text) => {
    onTasksChange(tasks.map((t, i) => (i === idx ? { ...t, text } : t)));
  };

  const handleTaskDelete = (idx) => {
    onTasksChange(tasks.filter((_, i) => i !== idx));
  };

  return (
    <NotesContainer>
      <ContentSection>
        <SectionCard>
          <SectionTitle>Meeting Summary</SectionTitle>
          <SummaryTextArea
            value={summary}
            onChange={(e) => onSummaryChange(e.target.value)}
            readOnly={readOnly}
            placeholder="Enter a summary of the meeting..."
          />
        </SectionCard>

        <SectionCard>
          <SectionTitle>Key Updates</SectionTitle>
          <UpdatesList>
            {updates.map((update, idx) => (
              <UpdateItem key={update.id || idx}>
                <UpdateIcon>{update.icon}</UpdateIcon>
                <UpdateContent>
                  <UpdateTitle>{update.label}</UpdateTitle>
                  <UpdateText
                    value={update.value}
                    onChange={(e) => {
                      const newUpdates = [...updates];
                      newUpdates[idx] = { ...update, value: e.target.value };
                      onUpdatesChange(newUpdates);
                    }}
                    readOnly={readOnly}
                  />
                </UpdateContent>
              </UpdateItem>
            ))}
          </UpdatesList>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Tasks</SectionTitle>
          <TaskList>
            {tasks.map((task, idx) => (
              <TaskItem key={task.id || idx}>
                <Checkbox
                  type="checkbox"
                  checked={task.done}
                  onChange={() => handleTaskToggle(idx)}
                  disabled={readOnly}
                />
                <TaskInput
                  value={task.text}
                  onChange={(e) => handleTaskEdit(idx, e.target.value)}
                  disabled={readOnly}
                />
                {!readOnly && (
                  <ActionButton onClick={() => handleTaskDelete(idx)}>
                    Delete
                  </ActionButton>
                )}
              </TaskItem>
            ))}
          </TaskList>
          {!readOnly && (
            <>
              <TaskInput
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="New task..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddTask();
                }}
              />
              <AddButton onClick={handleAddTask}>Add Task</AddButton>
            </>
          )}
        </SectionCard>
      </ContentSection>
      {!readOnly && (
        <Footer>
          <SaveButton onClick={onSave}>Save All</SaveButton>
        </Footer>
      )}
    </NotesContainer>
  );
};

NotesManager.propTypes = {
  summary: PropTypes.string.isRequired,
  onSummaryChange: PropTypes.func.isRequired,
  updates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onUpdatesChange: PropTypes.func.isRequired,
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string.isRequired,
      done: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  onTasksChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

NotesManager.defaultProps = {
  summary: "",
  updates: [],
  tasks: [],
  readOnly: false,
};

export default NotesManager;
